/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { useDocLoader } from '@/pages/DocsPage/useDocLoader';
import { DocSideBar } from '@/pages/DocsPage/DocsLayout/DocSideBar';
import { Heading, PanelMenuDoc } from '@/pages/DocsPage/DocsLayout/PanelMenuDoc';
import { DocModalSearch } from '@/pages/DocsPage/DocsLayout/DocModalSearch';
import { SearchResult } from 'minisearch';
import { DocBottomNavigation } from '@/pages/DocsPage/DocsLayout/DocBottomNavigation';
import { useTranslation } from 'react-i18next';
import { RE2JS } from 're2js';
import { Tag } from 'primereact/tag';

interface Props {
  appVersion?: string;
  children?: React.ReactNode;
}
export const DocsLayout: React.FC<Props> = ({ appVersion, children }) => {
  const DOC_WRAPPER_ID = 'doc-wrapper';

  const docRef = useRef<HTMLDivElement>(null);

  const [activeLink, setActiveLink] = useState<string>();

  const location = useLocation();

  const { manifest } = useDocLoader();

  const [searchResult, setSearchResult] = useState<SearchResult[]>([]);

  const [searchModalVisible, setSearchModalVisible] = useState(false);

  const [recapLinks, setRecapLinks] = useState<Heading[]>([]);

  const { t } = useTranslation();

  const onIntersect: IntersectionObserverCallback = (entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        const anchor = entry.target as HTMLAnchorElement;
        setActiveLink(anchor.hash);
      }
    });
  };

  const handleOnSelectResult = useCallback(() => {
    if (docRef.current && searchResult.length !== 0) {
      const searchOccur = Object.keys(searchResult[0].match);
      const matches = docRef.current.querySelectorAll(
        '*:not(script):not(style):not(title):not(section):not(div):not(ul)'
      );
      matches.forEach((el) => {
        if (searchOccur.some((occur) => el.textContent?.toLowerCase()?.includes(occur))) {
          el.innerHTML = searchOccur.reduce((acc, nex) => {
            const regex = new RE2JS(nex, RE2JS.CASE_INSENSITIVE | RE2JS.MULTILINE);
            return acc.replace(regex.pattern(), `<mark>${nex}</mark>`);
          }, el.innerHTML);
        }
      });
    }
  }, [docRef.current, searchResult]);

  useEffect(() => {
    const docNode = docRef.current;

    if (docNode) {
      const anchorLinks: NodeListOf<HTMLAnchorElement> = docNode.querySelectorAll('a[href^="#"][class="anchor-link"]');

      // The URL hash for a doc page looks like `#/docs/how-to/1-integration-guide#10-2-output-data-management`.
      // The heading anchor to scroll to is the fragment after the last `#`.
      const hashParts = window.location.hash.split('#').filter(Boolean);
      const targetId = hashParts.length > 1 ? hashParts[hashParts.length - 1] : undefined;

      let treeLinks: Heading[] = [];
      let targetLink: HTMLAnchorElement | undefined;

      anchorLinks.forEach((link) => {
        const level = link.attributes.getNamedItem('data-level');
        if (level) {
          treeLinks = [...treeLinks, { level: level.value, title: link.title, ref: link.hash } satisfies Heading];
        }
        if (targetId && link.id === targetId) {
          targetLink = link;
        }
      });

      setRecapLinks(treeLinks);

      if (targetLink) {
        requestAnimationFrame(() => {
          docNode.scrollTo({
            top: targetLink?.offsetTop,
            behavior: 'smooth'
          });
        });
      } else {
        docNode.scrollTo({
          top: 0,
          behavior: 'instant'
        });
      }

      const intersectObserver = new IntersectionObserver(onIntersect, {
        root: docNode,
        rootMargin: '0px 0px -70% 0px',
        threshold: 1
      });

      // Observe intersection
      anchorLinks.forEach((link) => intersectObserver.observe(link));

      if (searchResult.length !== 0) {
        handleOnSelectResult();
      }

      return () => intersectObserver.disconnect();
    }
  }, [location.pathname, location.hash, searchResult, handleOnSelectResult]);

  return (
    <section className="flex h-screen overflow-hidden justify-content-between">
      <nav className="border-right-2 top-0 flex flex-column  surface-border w-17rem sticky h-screen overflow-y-auto pb-7">
        <div
          onClick={() => setSearchModalVisible(true)}
          style={{ borderWidth: '0.5px', borderStyle: 'solid' }}
          className="mb-2 mt-3 flex align-items-center text-sm mx-2 px-4 py-2 cursor-pointer border-round-3xl"
        >
          <i className="pi pi-search mr-3 text-sm" />
          <span className="text-sm font-light">{t(['misc.search_here', 'Search here'])}</span>
        </div>
        <div className="h-full flex flex-column justify-content-between">
          <DocSideBar resetSearchResult={() => setSearchResult([])} manifest={manifest} />

          {appVersion && (
            <div className="flex mx-2 align-items-center justify-content-between py-2 surface-border border-top-1">
              <div className="flex align-items-center">
                <span className="text-md font-bold"> AntFlow</span>
                <Tag className="mx-2 text-xs" value={`v${appVersion}`} />
              </div>
            </div>
          )}
        </div>
      </nav>
      <div
        ref={docRef}
        id={DOC_WRAPPER_ID}
        className="flex-1 h-screen overflow-y-auto overflow-x-hidden text-left px-8 pb-8"
      >
        {children}
        <DocBottomNavigation resetSearchResult={() => setSearchResult([])} className="mt-8" />
      </div>
      <div className=" overflow-y-auto w-2 text-left mt-3">
        {recapLinks.length > 0 && (
          <PanelMenuDoc
            activeLink={activeLink}
            docWrapperId={DOC_WRAPPER_ID}
            recapTreeLinks={recapLinks}
            className="border-0 w-full pl-3"
          />
        )}
      </div>

      {searchModalVisible && (
        <DocModalSearch
          docWrapper={DOC_WRAPPER_ID}
          onSelectResult={handleOnSelectResult}
          searchResult={searchResult}
          setSearchResult={setSearchResult}
          visible={searchModalVisible}
          setVisible={setSearchModalVisible}
        />
      )}
    </section>
  );
};
